import { useEffect, useState } from "react";
import { CreditCard, Download, FileText, Loader2, LogOut } from "lucide-react";
import { useCustomerAuth } from "@/context/AuthContext";
import { API, api, formatApiErrorDetail } from "@/lib/api";
import { Logo } from "@/components/Logo";

const EmailAuthForm = () => {
  const { loginWithEmail, registerWithEmail } = useCustomerAuth();
  const [mode, setMode] = useState("login"); // login | register | forgot
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [resetSent, setResetSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (mode === "register") {
        await registerWithEmail(name, email, password);
      } else if (mode === "forgot") {
        await api.post("/auth/customer/forgot-password", { email, origin_url: window.location.origin });
        setResetSent(true);
      } else {
        await loginWithEmail(email, password);
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong — try again");
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full rounded-xl border border-white/10 bg-[#0B0B0E] px-4 py-3 text-sm text-white placeholder-zinc-600 outline-none transition-colors focus:border-emerald-300/60";

  if (mode === "forgot") {
    return (
      <form onSubmit={submit} className="mt-7 space-y-3 text-left" data-testid="forgot-password-form">
        {resetSent ? (
          <p className="rounded-xl border border-emerald-300/30 bg-emerald-300/10 px-4 py-3 text-xs leading-relaxed text-emerald-200" data-testid="forgot-password-sent">
            If an account exists for {email}, a reset link is on its way. Check your inbox (and spam) — the link is good for one hour.
          </p>
        ) : (
          <>
            <input
              type="email" required placeholder="Your account email" value={email} onChange={(e) => setEmail(e.target.value)}
              className={inputCls} data-testid="forgot-password-email" autoComplete="email"
            />
            {error && <p className="text-xs text-red-400" data-testid="forgot-password-error">{error}</p>}
            <button
              type="submit" disabled={busy}
              data-testid="forgot-password-submit"
              className="flex w-full items-center justify-center gap-2 rounded-full bg-emerald-300 py-3.5 text-sm font-bold text-black transition-colors hover:bg-emerald-200 disabled:opacity-50"
            >
              {busy && <Loader2 size={15} className="animate-spin" />} Email Me a Reset Link
            </button>
          </>
        )}
        <button
          type="button"
          onClick={() => { setMode("login"); setError(""); setResetSent(false); }}
          data-testid="forgot-password-back"
          className="w-full pt-1 text-center text-xs text-zinc-500 transition-colors hover:text-emerald-200"
        >
          Back to sign in
        </button>
      </form>
    );
  }

  return (
    <form onSubmit={submit} className="mt-7 space-y-3 text-left" data-testid="email-auth-form">
      {mode === "register" && (
        <input
          type="text" placeholder="Artist name" value={name} onChange={(e) => setName(e.target.value)}
          className={inputCls} data-testid="email-auth-name" autoComplete="name"
        />
      )}
      <input
        type="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)}
        className={inputCls} data-testid="email-auth-email" autoComplete="email"
      />
      <input
        type="password" required minLength={mode === "register" ? 8 : 1}
        placeholder={mode === "register" ? "Password (8+ characters)" : "Password"}
        value={password} onChange={(e) => setPassword(e.target.value)}
        className={inputCls} data-testid="email-auth-password"
        autoComplete={mode === "register" ? "new-password" : "current-password"}
      />
      {error && <p className="text-xs text-red-400" data-testid="email-auth-error">{error}</p>}
      <button
        type="submit" disabled={busy}
        data-testid="email-auth-submit"
        className="flex w-full items-center justify-center gap-2 rounded-full bg-emerald-300 py-3.5 text-sm font-bold text-black transition-colors hover:bg-emerald-200 disabled:opacity-50"
      >
        {busy && <Loader2 size={15} className="animate-spin" />}
        {mode === "register" ? "Create Account" : "Sign In"}
      </button>
      <div className="flex items-center justify-between pt-1">
        <button
          type="button"
          onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
          data-testid="email-auth-toggle"
          className="text-xs text-zinc-500 transition-colors hover:text-emerald-200"
        >
          {mode === "login" ? "New here? Create an account" : "Already have an account? Sign in"}
        </button>
        {mode === "login" && (
          <button
            type="button"
            onClick={() => { setMode("forgot"); setError(""); }}
            data-testid="forgot-password-link"
            className="text-xs text-zinc-500 transition-colors hover:text-emerald-200"
          >
            Forgot password?
          </button>
        )}
      </div>
    </form>
  );
};

export default function Account() {
  const { user, loginWithGoogle, logout } = useCustomerAuth();
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    if (user) {
      api.get("/account/orders").then((r) => setOrders(r.data)).catch(() => {});
    }
  }, [user]);

  if (user === null)
    return <div className="flex min-h-[60vh] items-center justify-center"><Loader2 className="animate-spin text-emerald-300" /></div>;

  if (!user)
    return (
      <div className="flex min-h-[70vh] items-center justify-center px-4 py-16" data-testid="account-login-page">
        <div className="w-full max-w-sm rounded-3xl border border-white/10 bg-[#141419] p-8 text-center">
          <div className="flex justify-center"><Logo size={52} /></div>
          <h1 className="font-heading mt-5 text-2xl font-bold text-white">Your Account</h1>
          <p className="mt-2 text-sm text-zinc-500">
            Sign in to access purchased beats and faster checkout with your saved card.
          </p>

          <EmailAuthForm />

          <div className="my-6 flex items-center gap-3">
            <span className="h-px flex-1 bg-white/10" />
            <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-600">or</span>
            <span className="h-px flex-1 bg-white/10" />
          </div>

          <button
            onClick={loginWithGoogle}
            data-testid="google-signin-button"
            className="flex w-full items-center justify-center gap-3 rounded-full bg-white py-3.5 text-sm font-bold text-black transition-colors hover:bg-zinc-200"
          >
            <svg width="17" height="17" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M23.5 12.27c0-.85-.08-1.66-.22-2.45H12v4.64h6.45a5.52 5.52 0 0 1-2.39 3.62v3h3.87c2.26-2.09 3.57-5.16 3.57-8.81z"/>
              <path fill="#34A853" d="M12 24c3.24 0 5.96-1.07 7.94-2.91l-3.87-3c-1.07.72-2.44 1.15-4.07 1.15-3.13 0-5.78-2.11-6.73-4.96H1.29v3.1A12 12 0 0 0 12 24z"/>
              <path fill="#FBBC05" d="M5.27 14.28A7.2 7.2 0 0 1 4.89 12c0-.79.14-1.56.38-2.28v-3.1H1.29a12 12 0 0 0 0 10.76l3.98-3.1z"/>
              <path fill="#EA4335" d="M12 4.76c1.76 0 3.34.61 4.58 1.79l3.44-3.44A11.98 11.98 0 0 0 12 0 12 12 0 0 0 1.29 6.62l3.98 3.1C6.22 6.87 8.87 4.76 12 4.76z"/>
            </svg>
            Continue with Google
          </button>
        </div>
      </div>
    );

  return (
    <div className="mx-auto max-w-4xl px-4 py-14 sm:px-6 lg:px-8" data-testid="account-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          {user.picture ? (
            <img src={user.picture} alt="" className="h-14 w-14 rounded-full border border-emerald-300/40" referrerPolicy="no-referrer" />
          ) : (
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-emerald-300 font-heading text-xl font-extrabold text-black">
              {(user.name || user.email)[0].toUpperCase()}
            </div>
          )}
          <div>
            <h1 className="font-heading text-2xl font-bold text-white" data-testid="account-name">{user.name || "Artist"}</h1>
            <p className="text-sm text-zinc-500">{user.email}</p>
          </div>
        </div>
        <button onClick={logout} data-testid="account-logout-button"
          className="flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs text-zinc-400 hover:text-white">
          <LogOut size={13} /> Sign out
        </button>
      </div>

      <div className="mt-8 flex items-start gap-3 rounded-2xl border border-white/10 bg-[#141419] p-4" data-testid="saved-card-note">
        <CreditCard size={17} className="mt-0.5 shrink-0 text-emerald-300" />
        <p className="text-xs leading-relaxed text-zinc-400">
          When you buy while signed in, Stripe securely remembers your card — your next lease is one tap. Card details never touch this site.
        </p>
      </div>

      <h2 className="font-heading mt-12 text-xl font-bold text-white">Your Purchased Beats</h2>
      <div className="mt-5 space-y-3" data-testid="account-orders-list">
        {orders.map((o) => (
          <div key={o.download_token} className="flex flex-wrap items-center gap-4 rounded-2xl border border-white/10 bg-[#141419] p-5" data-testid={`account-order-${o.download_token.slice(0, 8)}`}>
            <div className="min-w-0 flex-1">
              <p className="font-bold text-white">"{o.beat_title}"</p>
              <p className="font-mono text-[11px] uppercase tracking-wider text-emerald-300">{o.tier_name} · ${((o.amount || 0) / 100).toFixed(2)}</p>
            </div>
            <a href={`${API}/download/${o.download_token}`} data-testid={`account-download-audio-${o.download_token.slice(0, 8)}`}
              className="flex items-center gap-2 rounded-full bg-emerald-300 px-4 py-2 text-xs font-bold uppercase tracking-wider text-black hover:bg-emerald-200">
              <Download size={13} /> Audio
            </a>
            <a href={`${API}/download/${o.download_token}/license`} data-testid={`account-download-license-${o.download_token.slice(0, 8)}`}
              className="flex items-center gap-2 rounded-full border border-emerald-300/40 px-4 py-2 text-xs font-bold uppercase tracking-wider text-emerald-200 hover:bg-emerald-300/10">
              <FileText size={13} /> License
            </a>
          </div>
        ))}
        {orders.length === 0 && (
          <p className="rounded-2xl border border-dashed border-white/10 p-8 text-center text-sm text-zinc-600">
            No purchases on this email yet. Beats you buy with this email will show up here.
          </p>
        )}
      </div>
    </div>
  );
}
